import { useState, useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { trackPromoReveal, trackPromoCopy } from '../../utils/promoStats';
import { openPartnerLink } from '../../utils/openPartnerLink';
import { getBestDiscountLabel, getPromoActionLabel, getPromoBadgeLabel } from '../../utils/promoUi';
import { readPersistedStores } from '../../utils/storeSeed';
import { usePageSeo } from '../../utils/seo';

interface StorePromo {
  id: string;
  code: string;
  discount: string;
  description: string;
  expiryDate: string;
  isActive: boolean;
  isTopDeal?: boolean;
  image?: string;
  partnerUrl?: string;
}

interface Store {
  id: string;
  name: string;
  logo: string;
  description: string;
  category: string;
  subcategory?: string;
  rating: number;
  isActive: boolean;
  createdAt: string;
  promos: StorePromo[];
}

interface TopDeal extends StorePromo {
  storeName: string;
  storeCategory: string;
  views: number;
  featured: boolean;
}

export default function TopDeals() {
  usePageSeo({
    title: 'Топ акций и промокодов | Promokod.online',
    description: 'Лучшие предложения дня и недели на Promokod.online. Подборка самых выгодных акций, промокодов и скидок популярных магазинов.',
    canonicalPath: '/top-deals',
  });

  const [revealedCodes, setRevealedCodes] = useState<Set<string>>(new Set());
  const [selectedCategory, setSelectedCategory] = useState('Все');
  const [topDeals, setTopDeals] = useState<TopDeal[]>([]);
  const [categories, setCategories] = useState<string[]>(['Все']);

  useEffect(() => {
    // Загружаем магазины из localStorage
    const stores = readPersistedStores(localStorage) as Store[];
    
    // Собираем все топ-акции из активных магазинов
    const deals: TopDeal[] = [];
    const categorySet = new Set<string>();
    
    stores.forEach(store => {
      if (store.isActive) {
        categorySet.add(store.category);
        store.promos.forEach((promo, index) => {
          if (promo.isActive && promo.isTopDeal) {
            deals.push({
              ...promo,
              storeName: store.name,
              storeCategory: store.category,
              views: Math.floor(Math.random() * 10000) + 5000,
              featured: index === 0 // Первый промокод магазина - featured
            });
          }
        });
      }
    });

    setTopDeals(deals);
    setCategories(['Все', ...Array.from(categorySet).sort()]);
  }, []);

  const filteredDeals = selectedCategory === 'Все' 
    ? topDeals 
    : topDeals.filter(deal => deal.storeCategory === selectedCategory);

  const featuredDeals = topDeals.filter(deal => deal.featured).slice(0, 3);

  const handleGetDeal = (dealId: string, code: string, partnerUrl?: string) => {
    // Показываем промокод
    setRevealedCodes(prev => new Set([...prev, dealId]));
    
    // Копируем код в буфер обмена
    if (code) {
      navigator.clipboard.writeText(code);
    }

    // Трекинг
    const deal = [...topDeals, ...filteredDeals].find(d => d.id === dealId);
    if (deal) {
      const trackData = {
        promoId: dealId,
        promoCode: code,
        promoTitle: deal.description,
        storeName: deal.storeName,
        storeId: dealId,
        discount: deal.discount,
      };
      trackPromoReveal(trackData);
      trackPromoCopy(trackData);
    }

    openPartnerLink(partnerUrl);
  };

  const isCodeRevealed = (dealId: string) => revealedCodes.has(dealId);

  // Вычисляем максимальную скидку
  const maxDiscount = topDeals.length > 0
    ? getBestDiscountLabel(topDeals.map((deal) => deal.discount))
    : '0%';

  const totalViews = topDeals.reduce((sum, deal) => sum + deal.views, 0);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-orange-500 via-orange-600 to-red-600 text-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Топовые предложения недели
            </h1>
            <p className="text-xl text-orange-50 max-w-3xl mx-auto">
              Самые выгодные промокоды и акции с максимальными скидками до {maxDiscount}. Успейте воспользоваться!
            </p>
            <div className="flex items-center justify-center space-x-8 mt-8">
              <div className="text-center">
                <div className="text-3xl font-bold">{topDeals.length}</div>
                <div className="text-orange-100 text-sm">Активных акций</div>
              </div>
              <div className="w-px h-12 bg-orange-300"></div>
              <div className="text-center">
                <div className="text-3xl font-bold">{maxDiscount}</div>
                <div className="text-orange-100 text-sm">Макс. скидка</div>
              </div>
              <div className="w-px h-12 bg-orange-300"></div>
              <div className="text-center">
                <div className="text-3xl font-bold">{(totalViews / 1000).toFixed(0)}K+</div>
                <div className="text-orange-100 text-sm">Просмотров</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {featuredDeals.length > 0 && (
        <section className="py-12 bg-white">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center mb-8">
              <i className="ri-fire-fill text-3xl text-red-500 mr-3"></i>
              <h2 className="text-3xl font-bold text-gray-900">Горячие предложения</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredDeals.map((deal) => (
                <div
                  key={deal.id}
                  className="bg-gradient-to-br from-orange-50 to-red-50 rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 border-2 border-orange-200"
                >
                  {deal.image && (
                    <div className="relative">
                      <img
                        src={deal.image}
                        alt={deal.description}
                        className="w-full h-48 object-cover object-top"
                      />
                      <div className="absolute top-4 left-4">
                        <span className="bg-red-500 text-white px-4 py-2 rounded-full text-base font-bold shadow-lg">
                          {getPromoBadgeLabel(deal.discount)}
                        </span>
                      </div>
                      <div className="absolute top-4 right-4">
                        <span className="bg-white/95 text-gray-700 px-3 py-1 rounded-full text-xs font-medium">
                          <i className="ri-eye-line mr-1"></i>
                          {deal.views.toLocaleString()}
                        </span>
                      </div>
                      <div className="absolute bottom-4 left-4">
                        <span className="bg-orange-500 text-white px-3 py-1 rounded-full text-xs font-bold">
                          <i className="ri-fire-fill mr-1"></i>
                          ХИТ
                        </span>
                      </div>
                    </div>
                  )}

                  <div className="p-6">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-bold text-orange-600">{deal.storeName}</span>
                      <span className="text-xs text-gray-600 bg-white px-2 py-1 rounded-full">{deal.storeCategory}</span>
                    </div>

                    <h3 className="font-bold text-gray-900 mb-2 text-lg">{deal.description}</h3>

                    <div className="flex items-center justify-between mb-4 text-sm">
                      <span className="text-gray-500">
                        <i className="ri-time-line mr-1"></i>
                        До {deal.expiryDate}
                      </span>
                    </div>

                    <div className="flex items-center space-x-3">
                      <div className="flex-1 bg-white border-2 border-orange-200 rounded-lg px-4 py-3">
                        <div className="flex items-center justify-between">
                          <span className={`font-mono font-bold text-gray-900 ${!isCodeRevealed(deal.id) ? 'filter blur-sm select-none' : ''}`}>
                            {deal.code}
                          </span>
                          {isCodeRevealed(deal.id) && (
                            <button
                              onClick={() => navigator.clipboard.writeText(deal.code)}
                              className="text-orange-600 hover:text-orange-700 cursor-pointer"
                            >
                              <i className="ri-file-copy-line"></i>
                            </button>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => handleGetDeal(deal.id, deal.code, deal.partnerUrl)}
                        className="bg-orange-500 text-white px-6 py-3 rounded-lg hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap font-semibold shadow-md"
                      >
                        {isCodeRevealed(deal.id) ? '✓ Получено' : getPromoActionLabel(deal.code)}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Category Filter */}
      <section className="py-8 bg-gray-50 border-y border-gray-200">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Все предложения</h2>
            <span className="text-gray-600">{filteredDeals.length} акций</span>
          </div>
          
          <div className="flex flex-wrap gap-3">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-2 rounded-full font-medium transition-all cursor-pointer whitespace-nowrap ${
                  selectedCategory === category
                    ? 'bg-orange-500 text-white shadow-md'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* All Deals */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4">
          {filteredDeals.length === 0 ? (
            <div className="text-center py-16">
              <i className="ri-coupon-line text-6xl text-gray-300 mb-4"></i>
              <p className="text-gray-500 text-lg">В этой категории пока нет топ-акций</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredDeals.map((deal) => (
                <div
                  key={deal.id}
                  className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300"
                >
                  {deal.image && (
                    <div className="relative">
                      <img
                        src={deal.image}
                        alt={deal.description}
                        className="w-full h-48 object-cover object-top"
                      />
                      <div className="absolute top-4 left-4">
                          <span className="bg-red-500 text-white px-3 py-1 rounded-full text-sm font-bold">
                            {getPromoBadgeLabel(deal.discount)}
                        </span>
                      </div>
                      <div className="absolute top-4 right-4">
                        <span className="bg-white/90 text-gray-700 px-2 py-1 rounded-full text-xs">
                          <i className="ri-eye-line mr-1"></i>
                          {deal.views.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  )}

                  <div className="p-6">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-semibold text-blue-600">{deal.storeName}</span>
                      <span className="text-xs text-gray-500">{deal.storeCategory}</span>
                    </div>

                    <h3 className="font-bold text-gray-900 mb-2 text-lg">{deal.description}</h3>

                    <div className="flex items-center justify-between mb-4 text-sm">
                      <span className="text-gray-500">
                        <i className="ri-time-line mr-1"></i>
                        До {deal.expiryDate}
                      </span>
                    </div>

                    <div className="flex items-center space-x-3">
                      <div className="flex-1 bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
                        <div className="flex items-center justify-between">
                          <span className={`font-mono font-bold text-gray-900 ${!isCodeRevealed(deal.id) ? 'filter blur-sm select-none' : ''}`}>
                            {deal.code}
                          </span>
                          {isCodeRevealed(deal.id) && (
                            <button
                              onClick={() => navigator.clipboard.writeText(deal.code)}
                              className="text-blue-600 hover:text-blue-700 cursor-pointer"
                            >
                              <i className="ri-file-copy-line"></i>
                            </button>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => handleGetDeal(deal.id, deal.code, deal.partnerUrl)}
                        className="bg-orange-500 text-white px-6 py-3 rounded-lg hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap font-semibold"
                      >
                        {isCodeRevealed(deal.id) ? '✓' : getPromoActionLabel(deal.code)}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-blue-800 text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Не пропустите новые акции!</h2>
          <p className="text-xl text-blue-100 mb-8">
            Подпишитесь на уведомления и получайте лучшие предложения первыми
          </p>
          <button className="bg-white text-blue-600 px-8 py-4 rounded-full font-bold text-lg hover:bg-blue-50 transition-colors cursor-pointer whitespace-nowrap shadow-lg">
            Подписаться на уведомления
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
